/**
 * WebSocket Client — reconnection logic, structured logging, close-code handling.
 *
 * Close code contract
 * ─────────────────────────────────────────────────────────────────
 * 1000  Normal closure              → no reconnect
 * 1006  Abnormal / network drop     → reconnect (transient fault)
 * 4001  Auth failure (bad/expired)  → NO reconnect (caller must refresh token)
 * 4003  Forbidden (not a member)    → NO reconnect (permission issue)
 * ─────────────────────────────────────────────────────────────────
 */

const WS_CLOSE_REASONS = {
  1000: 'Normal closure',
  1001: 'Going away',
  1006: 'Abnormal closure (network error or server crash)',
  4001: 'Authentication failed — token invalid or expired',
  4003: 'Access forbidden — not a project member',
};

// Codes that must NOT trigger reconnection — they require user action.
const NO_RECONNECT_CODES = new Set([1000, 4001, 4003]);

class WebSocketClient {
  constructor(url, options = {}) {
    this.url = url;
    this.options = options;
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
    this.reconnectDelay = options.reconnectDelay || 1000;
    this.maxReconnectDelay = options.maxReconnectDelay || 30000;
    this.shouldReconnect = options.shouldReconnect !== false;
    this.onOpen = options.onOpen || null;
    this.onMessage = options.onMessage || null;
    this.onError = options.onError || null;
    this.onClose = options.onClose || null;
    this.isConnecting = false;
    this.isConnected = false;
  }

  connect() {
    if (this.isConnecting || this.isConnected) return;
    this.isConnecting = true;

    // Validate the URL has the correct ws:// or wss:// scheme.
    if (!this.url.startsWith('ws://') && !this.url.startsWith('wss://')) {
      const msg = `[WS] Invalid URL scheme — expected ws:// or wss://, got: ${this.url}`;
      console.error(msg);
      this.isConnecting = false;
      if (this.onError) this.onError(new Error(msg));
      return;
    }

    // Safety: port 443 must never be used with ws:// (only wss://)
    if (this.url.startsWith('ws://') && this.url.includes(':443')) {
      const msg = `[WS] Invalid URL — ws:// with port 443 is not allowed: ${this.url}`;
      console.error(msg);
      this.isConnecting = false;
      if (this.onError) this.onError(new Error(msg));
      return;
    }

    // Log the URL being used so production issues are immediately visible.
    const attempt = this.reconnectAttempts === 0
      ? 'initial'
      : `retry ${this.reconnectAttempts}/${this.maxReconnectAttempts}`;
    console.info(`[WS] Connecting (${attempt}): ${this.url}`);

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = (event) => {
        this.isConnecting = false;
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.info(`[WS] Connected: ${this.url}`);
        if (this.onOpen) this.onOpen(event);
      };

      this.ws.onmessage = (event) => {
        if (this.onMessage) this.onMessage(event);
      };

      this.ws.onerror = (error) => {
        this.isConnecting = false;
        console.error(`[WS] Error on ${this.url}:`, error);
        if (this.onError) this.onError(error);
      };

      this.ws.onclose = (event) => {
        this.isConnecting = false;
        this.isConnected = false;

        const reason = WS_CLOSE_REASONS[event.code] || event.reason || 'Unknown reason';
        console.warn(`[WS] Closed — code=${event.code} reason="${reason}" url=${this.url}`);

        if (this.onClose) this.onClose(event);

        // Never reconnect on auth / permission failures — those require user action.
        if (NO_RECONNECT_CODES.has(event.code)) {
          if (event.code === 4001) {
            console.error('[WS] Auth failure — stopping reconnection. Token must be refreshed.');
          } else if (event.code === 4003) {
            console.error('[WS] Permission denied — stopping reconnection.');
          }
          return;
        }

        // Exponential back-off reconnect for transient failures (1006, network drops, etc.)
        if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
            this.maxReconnectDelay
          );
          console.info(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})…`);
          setTimeout(() => this.connect(), delay);
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('[WS] Max reconnection attempts reached. Giving up.');
        }
      };

    } catch (error) {
      this.isConnecting = false;
      console.error('[WS] Exception creating WebSocket:', error);
      if (this.onError) this.onError(error);
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(typeof data === 'string' ? data : JSON.stringify(data));
    } else {
      console.warn('[WS] Cannot send — socket not open (state:', this.ws?.readyState, ')');
    }
  }

  close() {
    this.shouldReconnect = false;
    if (this.ws) {
      this.ws.close(1000, 'Client closing');
      this.ws = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
  }

  getReadyState() {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }
}

export default WebSocketClient;
